import React, { PropsWithChildren } from 'react';

import { theme } from '@garnish/constants';

import { AppContent } from './App.content';
import {
  ForceUpdateMode,
  LoadingAnimation,
  MaintenanceMode,
  PermissionsPrimer,
  useIsForceUpdateModeEnabled,
  useIsMaintenanceModeEnabled,
} from './components';
import {
  use3rdPartyIntegrations,
  useBackgroundLocationPermissionPrimer,
  useLocationPermissionPrimer,
  usePushNotificationPermissionPrimer,
} from './hooks';

// ────────────────────────────────────────────────────────────────────────────────

/**
 * Renders the post-gate render branches: force-update mode, maintenance mode,
 * the combined push + location permission primer flow or the regular app content.
 *
 * The launch-time gate (and the in-flight restart loading state) is owned by `LaunchGate` upstream.
 */
export const App = (props: PropsWithChildren) => {
  const { children } = props;

  // ─── Flags ───────────────────────────────────────────────────────────

  const isMaintenanceModeEnabled = useIsMaintenanceModeEnabled();
  const isForceUpdatedModeEnabled = useIsForceUpdateModeEnabled();

  // ─── Effects ─────────────────────────────────────────────────────────

  use3rdPartyIntegrations();

  // ─────────────────────────────────────────────────────────────────────

  if (isForceUpdatedModeEnabled) {
    return <ForceUpdateMode />;
  }

  if (isMaintenanceModeEnabled) {
    return <MaintenanceMode />;
  }

  return <AppContentWithPrimers>{children}</AppContentWithPrimers>;
};

// ─── Components ──────────────────────────────────────────────────────────────

/**
 * Mounted only past the force-update and maintenance checks, so primer
 * impressions are never tracked behind those screens.
 */
const AppContentWithPrimers = (props: PropsWithChildren) => {
  const { children } = props;

  const pushPrimer = usePushNotificationPermissionPrimer();
  const locationPrimer = useLocationPermissionPrimer();
  const backgroundLocationPrimer = useBackgroundLocationPermissionPrimer();

  // ─── Derived ─────────────────────────────────────────────────────────

  const isLoading =
    pushPrimer.isPushNotificationPermissionsLoading ||
    locationPrimer.isLocationPermissionsLoading ||
    backgroundLocationPrimer.isBackgroundLocationPermissionsLoading;
  const isPrimerActive =
    pushPrimer.isPrimerActive ||
    locationPrimer.isPrimerActive ||
    backgroundLocationPrimer.isPrimerActive;

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingAnimation withNavbarOffset backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  if (isPrimerActive) {
    return (
      <PermissionsPrimer
        pushPrimer={pushPrimer}
        locationPrimer={locationPrimer}
        backgroundLocationPrimer={backgroundLocationPrimer}
      />
    );
  }

  return <AppContent>{children}</AppContent>;
};
